import React, { useContext, useEffect, useState } from 'react';

import { useParams } from '@gatsbyjs/reach-router';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import JobLogButton from '../../components/job-log-button';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  apiRequestError, canReadEnrollmentFile, canWriteEnrollmentFile, checkUserAuthenticated,
  formatDateForDataGrid, goToRoutePage, setWindowTitle, triggerPreEnroll
} from '../../utils/helper';
import { AddFileIcon, MoreIcon, RefreshIcon, TriggerIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, FormControl, Grid, Hidden, IconButton, InputLabel, Link, ListItemIcon,
  ListItemText, Menu, MenuItem, Select, Tooltip, Typography
} from '../../utils/material-ui-core';
import UploadEnrollmentFile from './uploadEnrollmentFile';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const EnrollmentFiles = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, showAlert, userFunctionAccess, isUserAuthenticated, enrollmentBatches, refreshData } = useContext(AppContext);

  const { uploadDialog } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState<boolean>(false);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const [userKey, setUserKey] = React.useState("");
  const [enrollmentBatchFiles, setEnrollmentBatchFiles] = React.useState([]);

  const columns = [
    { field: "fileId", headerName: "File Id", width: 150, hide: false },
    { field: "statusName", headerName: "Status", width: 150, hide: false },
    {
      field: "clientCode",
      headerName: "Client Code",
      width: 180,
      hide: false,
    },
    {
      field: "clientName",
      headerName: "Client Name",
      width: 250,
      hide: false,
    },
    {
      field: "ready",
      headerName: "Pass",
      width: 150,
      type: "number",
      hide: false,
    },
    {
      field: "skipEnrollment",
      headerName: "Fail/Skip",
      width: 150,
      type: "number",
      hide: false,
    },
    {
      field: "inQueue",
      headerName: "Pending",
      width: 150,
      type: "number",
      hide: false,
    },
    {
      field: "totalRecord",
      headerName: "Total",
      width: 150,
      type: "number",
      hide: false,
    },
    {
      field: "inputFileName",
      headerName: "File Name",
      width: 400,
      hide: false,
    },
    {
      field: "lastModifiedBy",
      headerName: "Last Modified By",
      width: 250,
      hide: true,
    },
    {
      field: "lastModifiedDateTime",
      headerName: "Last Modified Date",
      width: 200,
      type: "dateTime",
      hide: false,
      valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string),
    },
    { field: "preBatchLoadKey", headerName: "Key", width: 350, hide: true },
  ];

  // Refresh datagrid data
  const refresh = async () => {
    setLoading(true);
    setEnrollmentBatchFiles([]);
    setTimeout(async () => {
      await refreshData("enrollmentBatches");
      setLoading(false);
    }, 500);
  };

  // Trigger job
  const triggerJob = async () => {
    if (!canWriteEnrollmentFile(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    try {
      const apiResponse = await triggerPreEnroll();
      if (apiResponse.success) {
        showAlert({
          message: "Successfully triggered.",
          severity: "success",
        });
      } else {
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    } catch (e) {
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  // Datagrid click event
  const handleDataGridClick = (params: GridRowParams, event: any) => {
    goToRoutePage("enrollment/files/" + params.row.preBatchLoadKey + "/review");
  };

  // Set data to datagrid
  const setDataGridData = () => {
    const rows: any = [];
    setEnrollmentBatchFiles(rows);

    const filterEnrollmentBatches = enrollmentBatches.filter((p) => p.userKey === userKey);
    if (filterEnrollmentBatches.length > 0) {
      if (filterEnrollmentBatches[0].enrollmentBatchFiles.length > 0) {
        filterEnrollmentBatches[0].enrollmentBatchFiles.map((enrollmentBatch, index) => rows.push({ id: index, ...enrollmentBatch }));
        setEnrollmentBatchFiles(rows);
      }
    }
  };

  // Open upload file dialog
  const displayFileUploadDialog = () => {
    setOpenFileUploadDialog(true);
  };

  // Confirmation from upload file dialog
  const confirmFileUploadDialog = async (confirmOutput: ConfirmOutput) => {
    setOpenFileUploadDialog(false);
    if (confirmOutput.status === "refresh") {
      await refresh();
    }
  };

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  // Userkey dropdown list change event
  const handleUserKeyChange = async (key: string) => {
    setUserKey(key);
    localStorage.setItem("EnrollmentBatchUserKey", key);
  };

  useEffect(() => {
    writeLog("Enrollment Files", "", "", "");
    setWindowTitle("Enrollment Files");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!canReadEnrollmentFile(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      var key = localStorage.getItem("EnrollmentBatchUserKey");

      if (key !== undefined && key !== null && key !== "") {
        handleUserKeyChange(key);
      }
      else {
        enrollmentBatches.forEach((enrollmentBatch, index) => {
          handleUserKeyChange(String(enrollmentBatch.userKey));
        })
      }

      if (uploadDialog !== undefined && uploadDialog !== null && uploadDialog !== "") {
        displayFileUploadDialog();
      }
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataGridData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentBatches, userKey]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <UploadEnrollmentFile open={openFileUploadDialog} onCallback={confirmFileUploadDialog} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs style={{ paddingTop: "10px" }}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Enrollment Files</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                  
                    <JobLogButton jobPrefix='enrollmentfileprocess' jobName='Enrollment File Process' mode={0} handleGridMoreMenuClose={() => { }} />

                    {
                      canWriteEnrollmentFile(userFunctionAccess) ? (
                        <IconButton>
                          <Tooltip arrow title={"Upload new enrollment file."}>
                            <AddFileIcon color="secondary" onClick={() => displayFileUploadDialog()} />
                          </Tooltip>
                        </IconButton>
                      )
                        : null
                    }

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>

                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>
                      {
                        canWriteEnrollmentFile(userFunctionAccess) ? (
                          <StyledMenuItem
                            onClick={(e) => {
                              triggerJob();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <TriggerIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"Trigger Job"} />
                          </StyledMenuItem>
                        ) : null
                      }

                      <JobLogButton jobPrefix='enrollmentfileprocess' jobName='Enrollment File Process' mode={1} handleGridMoreMenuClose={handleGridMoreMenuClose} />

                      {
                        canWriteEnrollmentFile(userFunctionAccess) ? (
                          <StyledMenuItem
                            onClick={(e) => {
                              displayFileUploadDialog();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <AddFileIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"Upload Enrollment File "} />
                          </StyledMenuItem>
                        ) : null
                      }

                      <StyledMenuItem
                        onClick={(e) => {
                          refresh();
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh "} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <Grid container spacing={3} className={globalStyles.formikContainer}>
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <FormControl className={globalStyles.formControl} fullWidth={true}>
                  <InputLabel shrink={true}>Username</InputLabel>
                  <Select
                    required
                    native
                    id="userKey"
                    name="userKey"
                    value={userKey}
                    onChange={(e) => {
                      handleUserKeyChange(e.target.value as string);
                    }}
                    label="Username"
                  >
                    <option value={""}></option>
                    {enrollmentBatches.map((enrollmentBatch, index) => (
                      <option value={enrollmentBatch.userKey} key={index}>
                        {enrollmentBatch.username}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box className={pageStyles.datagridContainer}>
                  <CustomDataGrid uniqueGridCode={"EnrollmentBatchListResult"} columns={columns} rows={enrollmentBatchFiles} showToolBar={true} sortModel={[{ field: "lastModifiedDateTime", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "350px",
      height: "calc(100vh - 350px)",
      width: "100%",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        height: "450px",
      },
    },
  })
);
export default EnrollmentFiles;
